import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { FARMER_MANAGER_ROLE_SLUG, LIGHT_USER_ROLE_SLUG } from '~/Constants'
import i18n from '~/components/i18n'
import { useAuth } from '~/routes/components/AuthProvider'
import { useDebounce } from 'use-lodash-debounce'
import { FarmersQuery } from '~/QueryHooks/Farmers'
import notify from '~/components/Notify'
import ProfileSettingsService from '~/api/services/profileSettings'
import Layout from '~/components/Layout'
import { FarmersService } from '~/api/services/farmers/index'
import TableComponent from '~/components/TableComponent'
import InlineDialog from '~/components/InlineDialog'
import CustomIcon from '~/components/CustomIcon'
import { Dropdown, Input, Popup } from 'semantic-ui-react'
import TwoFaComp from '~/components/TwoFaComp'
import Dialog from '~/components/Dialog'
import { handleErrors } from '~/utils/helpers'
import { FixedPricePrefixType } from '~/api/services/dashboard'

const defaultFilter = {
  page: 0,
  search: '',
  status: null,
  rows: 10
}

interface Props {
  statusOptions: any[]
  title: string
  tableColumnHeaders: any[]
  prefix: FixedPricePrefixType
}

const ActivityTable: React.FC<Props> = (props) => {
  const { statusOptions, title, tableColumnHeaders, prefix } = props
  const { t } = useTranslation()
  const { id } = useParams()
  const { systemData, systemData: { logged: { roleSlug } } } = useAuth()
  const tfaCancellationAmount = systemData?.roleSettings?.tfaCancellationAmount === 1
  const tfaType = systemData?.logged?.tfaType

  const [isBusy, setIsBusy] = useState(false)
  const [isLoadingBlockReq, setIsLoadingBlockReq] = useState(false)
  const [dataFiltered, setDataFiltered] = useState(defaultFilter)
  const [cancelTradingId, setCancelTradingId] = useState(null)
  const [correctTfaCode, setCorrectTfaCode] = useState(false)
  const [tfaCode, setTfaCode] = useState('')
  const [search, setSearch] = useState('')

  const debouncedValue = useDebounce(search, 300)
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const disableActions = (roleSlug === LIGHT_USER_ROLE_SLUG) || (roleSlug === FARMER_MANAGER_ROLE_SLUG)

  const actions = [
    {
      popupMessage: i18n.t('Cancellation'),
      type: 'icon',
      name: 'close',
      size: 'large',
      color: 'red',
      actionType: 'cancellation',
      display: lightUserLogged ? [] : ['completed'],
      displayedText: i18n.t('No actions')
    },
    {
      popupMessage: i18n.t('Delete offer'),
      type: 'icon',
      name: 'trash',
      size: 'large',
      verifiedActionKey: 'offerDeleteAvailable',
      verifiedAction: 1
    }
  ]

  const {
    data: tradingData,
    refetch: fetchData,
    isFetching: isFetchingData
  } = FarmersQuery.tradingActivitiesFarmer(id,
    prefix,
    { ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 }
  )

  useEffect(() => {
    fetchData()
  }, [dataFiltered, prefix, t])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  async function handleSendCode(id) {
    setCancelTradingId(id)
    if (tfaType === 'email' || tfaType === 'sms') {
      setIsBusy(true)
      try {
        await ProfileSettingsService.sendCode()
        notify(t('Code sent'), 'success')
        setCancelTradingId(id)
      } catch (error) {
        setCancelTradingId(null)
        handleErrors(error)
      }
      setIsBusy(false)
    }
  }

  function renderFilters() {
    return <div>
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        style={{ marginRight: 10 }}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
      <Dropdown
        placeholder={t('Filter by status')}
        options={statusOptions?.map(status => ({ value: status.key, text: status.label })) || []}
        clearable
        onChange={(e, data) => setDataFiltered({
          ...dataFiltered,
          status: data.value,
          page: 0
        })}
        selection
        selectOnBlur={false}
      />
    </div>

  }

  async function handleCancelation(row) {
    if (tfaCancellationAmount) {
      handleSendCode(row.id)
      return
    }
    fetchData()
    setCancelTradingId(row.id)
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'cancellation':
        return handleCancelation(row)
      default:
        return
    }
  }

  async function deleteOffer(offerId) {
    setIsBusy(true)
    try {
      await FarmersService.deleteFarmerOfferAmount(id, prefix, offerId)
      notify(t('Successfully deleted offer'), 'success');
      fetchData()
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderDeleteOfferColumn(offerId) {
    return <InlineDialog
      control={<CustomIcon name='trash' size='large' />}
      text={t('')}
      action={() => deleteOffer(offerId)} />
  }

  function renderDeleteOffer(row) {
    const noActions = !(!disableActions && row.status === 'completed') && row.offerDeleteAvailable !== 1
    return <>
      {!disableActions && row.status === 'completed' && <Popup
        content={t('Cancelattion')}
        trigger={<CustomIcon onClick={() => handleCancelation(row)} name='cancel' size='large' color='red' />}
      />
      }
      {Boolean(row.offerDeleteAvailable) && !disableActions && renderDeleteOfferColumn(row.id)}
      {(noActions || disableActions) && <span>{t('No actions')}</span>}
    </>
  }

  function renderColumn(type, row) {
    switch (type) {
      case 'delete-offer':
        return renderDeleteOffer(row)
      default:
        return
    }
  }

  function renderContent() {
    return <TableComponent
      actions={actions}
      animated
      isFetchingData={isFetchingData}
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
      renderColumn={(type, _, row) => renderColumn(type, row)}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      rows={rows}
      total={filtered}
    />
  }

  async function handleCancelAmount(data?) {
    setIsLoadingBlockReq(true)
    try {
      await FarmersService.blockTradingActivitiesFarmerAmount(id, cancelTradingId, prefix, data)
      notify(t('Farmer Amount blocked succesfully'), 'success')
      setCancelTradingId(null)
      setTfaCode('')
      fetchData()
    } catch (error) {
      handleErrors(error)
    }
    setIsLoadingBlockReq(false)
  }

  function renderCancellationModal() {
    return <Dialog
      isDialogOpen={Boolean(cancelTradingId)}
      textClose={t('No')}
      handleClose={() => {
        setTfaCode('')
        setCancelTradingId(null)
      }}
      isLoading={isLoadingBlockReq}
      disableSubmit={tfaCancellationAmount ? !correctTfaCode : false}
      textSubmit={t('Yes')}
      handleSubmit={() => handleCancelAmount(tfaCancellationAmount ? { code: tfaCode } : null)}
      textContent={<>
        <div>{t('Do you really want to cancel the amount?')}</div>
        {tfaCancellationAmount && <TwoFaComp
          onChangeTfaCode={(code) => setTfaCode(code)}
          onChangeCorrectTfaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
        />}
      </>}
      textHeader={t('Cancellation')}
      type={'abort'}
      size={tfaCancellationAmount ? 'small' : 'tiny'}
    />
  }

  const { filtered, rows } = tradingData ? tradingData : { filtered: 0, rows: [] }

  return <Layout
    fontSizeHeader={'18px'}
    isBusy={isBusy}
    useSpinner
    childContainerSize={'300px'}
    title={title}
    filters={renderFilters()}
  >
    {renderContent()}
    {cancelTradingId && renderCancellationModal()}
  </Layout>
}
export default ActivityTable